import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	TextField,
	Tooltip,
	Typography
} from '@material-ui/core';
import { showMessage } from 'app/store/fuse/messageSlice';
import ConfirmDialog from 'modules/ui/component/ConfirmDialog';
import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import PredictionModelEditService from '../service/PredictionModelEditService';
import PredictionTypeEditService from '../service/PredictionTypeEditService';

const MODEL_FILE_OPERATIONS_ENABLED = true;

const readFileAsBase64 = file =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = String(reader.result || '');
			const separatorIndex = result.indexOf(',');
			resolve(separatorIndex >= 0 ? result.substring(separatorIndex + 1) : result);
		};
		reader.onerror = () => reject(new Error('The model file could not be read.'));
		reader.readAsDataURL(file);
	});

const normalizeMetadata = metadata => {
	const parsed = JSON.parse(metadata);
	if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
		throw new Error('Metadata must be a JSON object.');
	}
	return JSON.stringify(parsed);
};

const formatFileSize = size => {
	if (!Number.isFinite(size) || size < 1024) return `${size || 0} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getValidationMessage = result => {
	if (!result || !result.properties) return null;
	const propertyKeys = Object.keys(result.properties);
	for (let keyIndex = 0; keyIndex < propertyKeys.length; keyIndex += 1) {
		const validations = result.properties[propertyKeys[keyIndex]];
		if (Array.isArray(validations) && validations.length > 0 && validations[0].message) {
			return validations[0].message;
		}
	}
	return null;
};

function PredictionModelActions(props) {
	const { getValues } = useFormContext();
	const modelPath = props.fieldPrefix.endsWith('.') ? props.fieldPrefix.slice(0, -1) : props.fieldPrefix;
	const model = getValues(modelPath);

	return (
		<div className="flex items-center whitespace-nowrap">
			{MODEL_FILE_OPERATIONS_ENABLED ? (
				<Tooltip title="Replace model file">
					<span>
						<IconButton
							disabled={props.fieldConfig.busy}
							onClick={() => props.fieldConfig.onReplace(model)}
						>
							<Icon>upload_file</Icon>
						</IconButton>
					</span>
				</Tooltip>
			) : null}
			<Tooltip title="Delete model">
				<span>
					<IconButton
						disabled={props.fieldConfig.busy}
						onClick={() => props.fieldConfig.onDelete(model)}
					>
						<Icon>delete</Icon>
					</IconButton>
				</span>
			</Tooltip>
		</div>
	);
}

function PredictionModelsField(props) {
	const dispatch = useDispatch();
	const routeParams = useParams();
	const { getValues, setValue } = useFormContext();
	const [gridKey, setGridKey] = useState(0);
	const [dialogMode, setDialogMode] = useState(null);
	const [selectedModel, setSelectedModel] = useState(null);
	const [modelName, setModelName] = useState('');
	const [metadata, setMetadata] = useState('');
	const [modelFile, setModelFile] = useState(null);
	const [dialogError, setDialogError] = useState(null);
	const [deleteModel, setDeleteModel] = useState(null);
	const [busy, setBusy] = useState(false);

	const routeId = routeParams.id === 'new' ? null : Number(routeParams.id);
	const predictionTypeId = Number.isFinite(routeId) ? routeId : null;

	const replaceModels = models => {
		setValue('models', models, { shouldDirty: true, shouldValidate: true });
		setGridKey(value => value + 1);
	};

	const refreshModels = async () => {
		const localModels = getValues('models') || [];
		const predictionType = await PredictionTypeEditService.getData(predictionTypeId);
		const mergedModels = predictionType.models.map(serverModel => {
			const localModel = localModels.find(model => model.id === serverModel.id);
			return localModel
				? { ...serverModel, name: localModel.name, metadataJson: localModel.metadataJson }
				: serverModel;
		});
		replaceModels(mergedModels);
	};

	const openUploadDialog = () => {
		setSelectedModel(null);
		setModelName('');
		setMetadata('');
		setModelFile(null);
		setDialogError(null);
		setDialogMode('upload');
	};

	const openReplaceDialog = model => {
		setSelectedModel(model);
		setModelName(model.name || '');
		setMetadata(model.metadataJson || '');
		setModelFile(null);
		setDialogError(null);
		setDialogMode('replace');
	};

	const closeDialog = () => {
		if (!busy) setDialogMode(null);
	};

	const submitModel = async () => {
		setDialogError(null);
		if (dialogMode === 'upload' && !modelName.trim()) {
			setDialogError('Model name is required.');
			return;
		}
		if (!modelFile) {
			setDialogError(dialogMode === 'replace' ? 'Select the replacement model file.' : 'Select a model file.');
			return;
		}

		let normalizedMetadata;
		try {
			normalizedMetadata = normalizeMetadata(metadata);
		} catch (error) {
			setDialogError(error instanceof SyntaxError ? 'Metadata is not valid JSON.' : error.message);
			return;
		}

		setBusy(true);
		try {
			const modelBase64 = await readFileAsBase64(modelFile);
			const result =
				dialogMode === 'replace'
					? await PredictionModelEditService.modify(selectedModel.id, modelBase64, normalizedMetadata)
					: await PredictionModelEditService.upload(
							predictionTypeId,
							modelName.trim(),
							modelBase64,
							normalizedMetadata
					  );

			if (!result.successful) {
				throw new Error(getValidationMessage(result) || 'The model operation failed.');
			}

			if (dialogMode === 'replace') {
				const models = (getValues('models') || []).map(model =>
					model.id === selectedModel.id ? { ...model, metadataJson: normalizedMetadata } : model
				);
				replaceModels(models);
			} else {
				await refreshModels();
			}

			dispatch(
				showMessage({
					message: dialogMode === 'replace' ? 'Model file replaced successfully.' : 'Model uploaded successfully.'
				})
			);
			setDialogMode(null);
		} catch (error) {
			setDialogError(error?.response?.data?.message || error.message || 'The model operation failed.');
		} finally {
			setBusy(false);
		}
	};

	const handleDelete = async model => {
		setBusy(true);
		try {
			const result = await PredictionModelEditService.deleteById(model.id);
			if (!result.successful) {
				throw new Error(getValidationMessage(result) || 'The model could not be deleted.');
			}
			replaceModels((getValues('models') || []).filter(item => item.id !== model.id));
			dispatch(showMessage({ message: 'Model deleted successfully.' }));
		} catch (error) {
			dispatch(showMessage({ message: error?.response?.data?.message || error.message || 'The model could not be deleted.' }));
		} finally {
			setBusy(false);
			setDeleteModel(null);
		}
	};

	const gridConfig = {
		...props.fieldConfig,
		modifyOnly: true,
		fields: [
			...props.fieldConfig.fields,
			{
				key: 'modelFile',
				text: MODEL_FILE_OPERATIONS_ENABLED ? 'Model file' : 'Actions',
				component: PredictionModelActions,
				isControlledInput: false,
				busy,
				onReplace: openReplaceDialog,
				onDelete: model => setDeleteModel(model)
			}
		]
	};

	return (
		<div>
			<EditFormGridComponent key={gridKey} {...props} fieldConfig={gridConfig} />
			{MODEL_FILE_OPERATIONS_ENABLED ? (
				<div className="flex flex-col items-end pt-24">
					{predictionTypeId === null ? (
						<Typography className="mb-12" color="textSecondary">
							Save the prediction type before uploading its first model.
						</Typography>
					) : null}
					<Button
						variant="contained"
						color="secondary"
						disabled={predictionTypeId === null || busy}
						onClick={openUploadDialog}
						startIcon={<Icon>cloud_upload</Icon>}
					>
						Upload model
					</Button>
				</div>
			) : null}

			<Dialog open={MODEL_FILE_OPERATIONS_ENABLED && dialogMode !== null} fullWidth maxWidth="md" onClose={closeDialog}>
				<DialogTitle className="py-12 border-0 border-b border-solid border-gray-200">
					<div className="flex items-center">
						<div className="flex items-center justify-center w-40 h-40 mr-12 rounded-full bg-gray-100">
							<Icon color="primary">{dialogMode === 'replace' ? 'published_with_changes' : 'cloud_upload'}</Icon>
						</div>
						<div>
							<Typography className="font-medium" variant="subtitle1">
								{dialogMode === 'replace' ? `Replace ${modelName}` : 'Upload model'}
							</Typography>
							<Typography variant="body2" color="textSecondary">
								{dialogMode === 'replace'
									? 'Replace the binary file and update its metadata.'
									: 'Create a model from a binary file and metadata JSON.'}
							</Typography>
						</div>
					</div>
				</DialogTitle>
				<DialogContent className="pt-24">
					<div className="p-20 mb-20 border border-solid border-gray-200 rounded-12">
						<div className="flex items-center mb-16">
							<Icon className="mr-8" color="action">settings</Icon>
							<Typography className="font-medium">Model settings</Typography>
						</div>
						<TextField
							className="mb-16"
							label="Name"
							value={modelName}
							onChange={event => setModelName(event.target.value)}
							disabled={dialogMode === 'replace' || busy}
							required
							variant="outlined"
							fullWidth
						/>
						<TextField
							label="Metadata JSON"
							value={metadata}
							onChange={event => setMetadata(event.target.value)}
							disabled={busy}
							required
							variant="outlined"
							fullWidth
							multiline
							rows={10}
						/>
					</div>

					<div className="flex flex-row items-center px-20 py-16 border-2 border-dashed border-gray-300 rounded-12">
						<Icon className="mr-16 text-36" color="action">
							{modelFile ? 'insert_drive_file' : 'cloud_upload'}
						</Icon>
						<div className="flex-1 min-w-0 text-left">
							<Typography className="font-medium truncate">
								{modelFile ? modelFile.name : 'Select the binary model file'}
							</Typography>
							<Typography variant="body2" color="textSecondary">
								{modelFile ? formatFileSize(modelFile.size) : 'The file is converted to Base64 before upload.'}
							</Typography>
						</div>
						<Button className="ml-16 whitespace-nowrap" component="label" variant="outlined" color="primary" disabled={busy} startIcon={<Icon>folder_open</Icon>}>
							{modelFile ? 'Choose another file' : 'Choose file'}
							<input
								key={`${dialogMode}-${selectedModel ? selectedModel.id : 'new'}`}
								type="file"
								hidden
								disabled={busy}
								onChange={event => setModelFile(event.target.files && event.target.files[0])}
							/>
						</Button>
					</div>

					{dialogError ? (
						<div className="flex items-center p-12 mt-16 border border-solid border-red-200 rounded-8 bg-red-50">
							<Icon className="mr-8" color="error">error_outline</Icon>
							<Typography className="text-red-500">{dialogError}</Typography>
						</div>
					) : null}
				</DialogContent>
				<DialogActions className="px-24 py-16 border-0 border-t border-solid border-gray-200 bg-gray-100">
					<Button className="mr-8" onClick={closeDialog} disabled={busy} startIcon={<Icon>close</Icon>}>
						Cancel
					</Button>
					<Button
						onClick={submitModel}
						disabled={busy}
						color="primary"
						variant="contained"
						startIcon={<Icon>{busy ? 'hourglass_empty' : dialogMode === 'replace' ? 'published_with_changes' : 'cloud_upload'}</Icon>}
					>
						{dialogMode === 'replace' ? 'Replace' : 'Upload'}
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				show={deleteModel !== null}
				title="Delete model"
				description={`Are you sure you want to delete ${deleteModel ? deleteModel.name : 'this model'}?`}
				onYes={() => handleDelete(deleteModel)}
				onClose={() => setDeleteModel(null)}
			/>
		</div>
	);
}

export default PredictionModelsField;
